/**
 * Moteur de calcul financier pour le module Planification (PRO).
 * Logique économiquement correcte : pas de "ROI" basé uniquement sur la valeur.
 * - Création de valeur
 * - Capital réellement immobilisé (coût net)
 * - Rendement réel basé sur les revenus uniquement
 */

/**
 * Calcule les indicateurs financiers d'un projet de planification.
 * @param {Object} project - Projet (estimated_cost, actual_cost, value_increase_percent, rent_increase_percent)
 * @param {Object} property - Propriété (purchase_price, total_monthly_revenue ou unit_revenues)
 * @param {{ useActualCost: boolean }} options - useActualCost: true pour utiliser le coût réel (projet terminé)
 * @returns {Object} Métriques (création de valeur, coût net, taux récupération, rendement réel, payback)
 */
export function calculatePlanningMetrics(project, property, options = {}) {
  const useActualCost = options.useActualCost === true;

  const defaultValue = {
    valueCreation: 0,
    immediateNetResult: 0,
    recoveryRate: null,
    netCost: 0,
    additionalAnnualIncome: 0,
    realYield: null,
    paybackYears: null,
    annualRevenueBefore: 0
  };

  if (!project || !property) return defaultValue;

  const projectCost = useActualCost
    ? parseFloat(project?.actual_cost) || 0
    : parseFloat(project?.estimated_cost) || 0;
  const valueIncreasePercent = parseFloat(project?.value_increase_percent) || 0;
  const rentIncreasePercent = parseFloat(project?.rent_increase_percent) || 0;

  const valueBefore = parseFloat(property?.purchase_price) || 0;
  const monthlyRent =
    parseFloat(property?.total_monthly_revenue) ||
    parseFloat(property?.monthly_revenue) ||
    (Array.isArray(property?.unit_revenues)
      ? property.unit_revenues.reduce((sum, r) => sum + (parseFloat(r) || 0), 0)
      : 0) ||
    0;
  const annualRevenueBefore = monthlyRent * 12;

  const valueCreation =
    valueBefore > 0 && valueIncreasePercent > 0
      ? valueBefore * (valueIncreasePercent / 100)
      : 0;
  const immediateNetResult = valueCreation - projectCost;
  const recoveryRate =
    projectCost > 0 && valueCreation > 0 ? valueCreation / projectCost : null;
  let netCost = projectCost - valueCreation;
  if (netCost < 0) netCost = 0;

  const additionalAnnualIncome =
    annualRevenueBefore > 0 && rentIncreasePercent > 0
      ? annualRevenueBefore * (rentIncreasePercent / 100)
      : 0;

  let realYield = null;
  if (netCost > 0 && additionalAnnualIncome > 0) {
    realYield = additionalAnnualIncome / netCost;
  }
  let paybackYears = null;
  if (netCost > 0 && additionalAnnualIncome > 0) {
    paybackYears = netCost / additionalAnnualIncome;
  }

  return {
    valueCreation: isNaN(valueCreation) ? 0 : valueCreation,
    immediateNetResult: isNaN(immediateNetResult) ? 0 : immediateNetResult,
    recoveryRate: recoveryRate != null && !isNaN(recoveryRate) ? recoveryRate : null,
    netCost: isNaN(netCost) ? 0 : netCost,
    additionalAnnualIncome: isNaN(additionalAnnualIncome) ? 0 : additionalAnnualIncome,
    realYield: realYield != null && !isNaN(realYield) ? realYield : null,
    paybackYears: paybackYears != null && !isNaN(paybackYears) ? paybackYears : null,
    annualRevenueBefore: isNaN(annualRevenueBefore) ? 0 : annualRevenueBefore,
    projectCost,
    valueBefore
  };
}

export function getPlannedMetrics(project, property) {
  return calculatePlanningMetrics(project, property, { useActualCost: false });
}

export function getActualMetrics(project, property) {
  return calculatePlanningMetrics(project, property, { useActualCost: true });
}

export default {
  calculatePlanningMetrics,
  getPlannedMetrics,
  getActualMetrics
};
